// Set per environment in .env.development / .env.production.
const MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY;
// Projects JSON feed. When empty (dev), the bundled sample data is used instead.
const PROJECTS_URL = process.env.PROJECTS_URL;

// "DEMO_MAP_ID" is Google's placeholder map ID, enough for AdvancedMarkerElement in development.
const MAP_ID = "DEMO_MAP_ID";
const DEFAULT_CENTER = { lat: -37, lng: 145.5 };
const DEFAULT_ZOOM = 7;
const FIT_PADDING = 60;
const FALLBACK_TEXT = "The map is unavailable right now.";

// "Under construction" / "under_construction" -> "under-construction"
const statusSlug = (value = "") =>
  String(value).trim().toLowerCase().replace(/[\s_]+/g, "-");

// "under-construction" -> "Under construction"
const humaniseStatus = (value = "") => {
  const text = statusSlug(value).replace(/-+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Status drives the pin and card colour. Unknown statuses fall back to operational.
const KNOWN_STATUSES = ["operational", "under-construction", "in-development"];
const statusModifier = (status) =>
  KNOWN_STATUSES.includes(status) ? status : "operational";

// Google's inline bootstrap loader (dynamic library import). It defines
// google.maps.importLibrary straight away and only injects the Maps script the first
// time a library is requested, so it is safe to call however many maps are on the page.
function installMapsBootstrap(params) {
  const google = window.google || (window.google = {});
  const maps = google.maps || (google.maps = {});
  if (maps.importLibrary) return;

  const requested = new Set();
  const query = new URLSearchParams();
  let loading = null;

  const load = () => {
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      query.set("libraries", [...requested].join(","));
      Object.keys(params).forEach((name) =>
        query.set(name.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), params[name]),
      );
      query.set("callback", "google.maps.__ib__");
      maps.__ib__ = resolve;
      script.src = `https://maps.googleapis.com/maps/api/js?${query}`;
      script.onerror = () => {
        // Allow a retry on the next importLibrary call
        loading = null;
        reject(new Error("The Google Maps JavaScript API could not load."));
      };
      document.head.appendChild(script);
    });
    return loading;
  };

  maps.importLibrary = (library, ...args) => {
    requested.add(library);
    return load().then(() => maps.importLibrary(library, ...args));
  };
}

function loadGoogleMaps() {
  if (!MAPS_API_KEY) return Promise.reject(new Error("GOOGLE_MAPS_API_KEY is not set"));
  installMapsBootstrap({ key: MAPS_API_KEY, v: "weekly" });
  return Promise.resolve();
}

function fetchProjects() {
  if (PROJECTS_URL) {
    return fetch(PROJECTS_URL).then((res) => {
      if (!res.ok) throw new Error(`Projects request failed: ${res.status}`);
      return res.json();
    });
  }

  // Sample data is for local development only; this branch is stripped from production builds.
  if (process.env.NODE_ENV !== "production") {
    return import("../../../../assets/data/sample/projects.sample.json").then(
      (module) => module.default || module,
    );
  }

  return Promise.reject(new Error("PROJECTS_URL is not set"));
}

// Keeps only projects that can be placed on the map
const normaliseProjects = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((item) => ({
      title: item && item.title,
      description: item && item.description,
      location: item && item.location,
      tag: item && item.tag,
      status: statusSlug(item && item.status),
      url: String((item && item.url) || "").trim(),
      position: { lat: Number(item && item.lat), lng: Number(item && item.lng) },
    }))
    .filter(
      (project) =>
        project.title &&
        Number.isFinite(project.position.lat) &&
        Number.isFinite(project.position.lng),
    );

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

// Only http(s) and relative links are allowed in the card
const safeUrl = (value) => {
  try {
    const url = new URL(value, window.location.href);
    return url.protocol === "http:" || url.protocol === "https:" ? value : "";
  } catch (error) {
    return "";
  }
};

const buildPin = (project) =>
  el("div", `redesign-project-map__pin is-${statusModifier(project.status)}`);

function buildCloseButton(onClose) {
  const button = el("button", "redesign-project-map__card-close");
  button.type = "button";
  button.setAttribute("aria-label", "Close");
  button.innerHTML =
    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  button.addEventListener("click", onClose);
  return button;
}

function buildCard(project, onClose) {
  const modifier = statusModifier(project.status);
  const card = el("div", "redesign-project-map__card");
  card.appendChild(buildCloseButton(onClose));

  if (project.status) {
    const status = el("p", `redesign-project-map__card-status is-${modifier}`);
    status.append(
      el("span", "redesign-project-map__card-dot"),
      document.createTextNode(humaniseStatus(project.status)),
    );
    card.appendChild(status);
  }

  card.appendChild(el("h3", "redesign-project-map__card-title", project.title));

  const meta = [project.location, project.tag].filter(Boolean).join(" · ");
  if (meta) card.appendChild(el("p", "redesign-project-map__card-meta", meta));

  if (project.description) {
    card.appendChild(el("p", "redesign-project-map__card-text", project.description));
  }

  const href = safeUrl(project.url);
  if (href) {
    const link = el("a", "redesign-project-map__card-link", "View project");
    link.href = href;
    link.target = "_self";
    card.appendChild(link);
  }

  return card;
}

async function renderMap(canvas, projects) {
  const [{ Map, InfoWindow }, { AdvancedMarkerElement }, { LatLngBounds }] =
    await Promise.all([
      window.google.maps.importLibrary("maps"),
      window.google.maps.importLibrary("marker"),
      window.google.maps.importLibrary("core"),
    ]);

  const map = new Map(canvas, {
    mapId: MAP_ID,
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    gestureHandling: "cooperative",
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
  });

  const infoWindow = new InfoWindow({
    maxWidth: 320,
    // The card draws its own close button, so Google's header row isn't needed
    headerDisabled: true,
  });
  const bounds = new LatLngBounds();

  projects.forEach((project) => {
    const marker = new AdvancedMarkerElement({
      map,
      position: project.position,
      title: project.title,
      content: buildPin(project),
      gmpClickable: true,
    });

    marker.addListener("click", () => {
      infoWindow.setContent(buildCard(project, () => infoWindow.close()));
      infoWindow.open({ map, anchor: marker });
    });

    bounds.extend(project.position);
  });

  map.addListener("click", () => infoWindow.close());

  if (projects.length === 1) {
    map.setCenter(projects[0].position);
    map.setZoom(10);
  } else if (projects.length > 1) {
    map.fitBounds(bounds, FIT_PADDING);
  }
}

function showFallback(canvas) {
  canvas.classList.add("is-unavailable");
  canvas.textContent = "";
  canvas.appendChild(el("p", "redesign-project-map__fallback", FALLBACK_TEXT));
}

export function projectMap(root = document) {
  root.querySelectorAll("[data-project-map]").forEach((section) => {
    if (section.__projectMapInitialized) return;
    section.__projectMapInitialized = true;

    const canvas = section.querySelector("[data-project-map-canvas]");
    if (!canvas) return;

    Promise.all([loadGoogleMaps(), fetchProjects()])
      .then(([, rows]) => renderMap(canvas, normaliseProjects(rows)))
      .catch((err) => {
        console.error("Project map failed to load", err);
        showFallback(canvas);
      });
  });
}
